// KnockoutContext.js
// The Knockout draft session state machine — web mirror of the session/timer
// half of KnockoutManager.swift.
//
// Why a context (not per-screen state): the in-memory session must survive
// navigating between the overview and the draft screen, exactly like the single
// app-lifetime KnockoutManager on iOS. That's what makes the three start paths
// behave correctly:
//   PATH 1  session already live in memory      → return it (back out + re-enter)
//   PATH 2  Firestore session, none in memory   → forfeit (0) + block (tab was
//                                                  refreshed/closed = "app kill")
//   PATH 3  no session anywhere                 → fresh 90s session
// Because memory only clears on a tab refresh/close, "session in Firestore but
// not in memory" reliably means the web equivalent of an iOS app-kill — so the
// anti-cheat forfeit matches iOS and a plain in-app navigation never forfeits.
//
// The timer is anchored to the server expiresAt so backgrounding/visibility
// changes can't extend it. No localStorage resume by design (matches iOS).

import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import {
  loadDraftSession,
  startDraftSession as writeDraftSession,
  submitEntry,
  createStanding,
  updateStandingDrafted,
  loadStanding,
} from "./knockoutService";

const DRAFT_SECONDS = 90;

const KnockoutContext = createContext(null);

export function KnockoutProvider({ children }) {
  const { user } = useAuth();

  const [draftSession, setDraftSession] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [draftedPlayers, setDraftedPlayers] = useState([]);
  const [currentPositionIndex, setCurrentPositionIndex] = useState(0);
  const [sessionDidExpire, setSessionDidExpire] = useState(false);

  // Refs mirror state so the timer/visibility callbacks never read stale values.
  const sessionRef = useRef(null);
  const draftedRef = useRef([]);
  const positionsRef = useRef([]); // resolved display positions, kept for submit
  const intervalRef = useRef(null);
  const expiredRef = useRef(false);

  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const clearSession = useCallback(() => {
    stopTimer();
    sessionRef.current = null;
    draftedRef.current = [];
    positionsRef.current = [];
    expiredRef.current = false;
    setDraftSession(null);
    setDraftedPlayers([]);
    setCurrentPositionIndex(0);
    setTimeRemaining(0);
  }, [stopTimer]);

  // Low-level: write the entry + standing for a given pool/day. Create the
  // standing on first entry, otherwise update only the two client-allowed keys.
  // Mirrors KnockoutManager.submitScore. Best-effort (errors logged, not thrown)
  // so a timeout path still navigates cleanly.
  const writeScore = useCallback(
    async ({ poolID, sportId, dayNumber, teamRating, chemistryPoints, draftedPlayers: players, draftedPositions, timedOut }) => {
      if (!user) return;
      try {
        await submitEntry({
          poolID,
          sportId,
          userID: user.uid,
          username: user.username,
          dayNumber,
          teamRating,
          chemistryPoints,
          draftedPlayers: players,
          draftedPositions,
          timedOut,
        });
      } catch (err) {
        console.error("🥊 Knockout: submitEntry failed —", err);
      }
      try {
        const existing = await loadStanding(poolID, user.uid);
        if (!existing) {
          await createStanding({
            poolID,
            sportId,
            userID: user.uid,
            username: user.username,
            currentDayScore: teamRating,
            highestDayReached: dayNumber,
          });
        } else {
          await updateStandingDrafted(poolID, user.uid, teamRating);
        }
      } catch (err) {
        console.error("🥊 Knockout: standing write failed —", err);
      }
    },
    [user]
  );

  // Fired when the 90s timer hits zero. Submits a 0 and signals the draft screen
  // to navigate away. Guarded so it can only run once per session.
  const handleExpiry = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || expiredRef.current) return;
    expiredRef.current = true;
    stopTimer();
    setSessionDidExpire(true);
    await writeScore({
      poolID: session.poolID,
      sportId: session.sportId,
      dayNumber: session.dayNumber,
      teamRating: 0,
      chemistryPoints: 0,
      draftedPlayers: [],
      draftedPositions: [],
      timedOut: true,
    });
    sessionRef.current = null;
    setDraftSession(null);
  }, [stopTimer, writeScore]);

  const startTimer = useCallback(
    (expiresAt) => {
      stopTimer();
      const tick = () => {
        const remaining = (expiresAt.getTime() - Date.now()) / 1000;
        setTimeRemaining(Math.max(0, remaining));
        if (remaining <= 0) {
          stopTimer();
          handleExpiry();
        }
      };
      tick();
      intervalRef.current = setInterval(tick, 1000);
    },
    [stopTimer, handleExpiry]
  );

  // Re-check expiry when the tab becomes visible again — the server expiresAt is
  // the only source of truth, so a backgrounded tab can't gain extra time.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState !== "visible") return;
      const session = sessionRef.current;
      if (!session || expiredRef.current) return;
      const remaining = (session.expiresAt.getTime() - Date.now()) / 1000;
      if (remaining <= 0) handleExpiry();
      else startTimer(session.expiresAt);
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [handleExpiry, startTimer]);

  // Clean up the interval if the provider ever unmounts.
  useEffect(() => () => stopTimer(), [stopTimer]);

  // Clear everything if the signed-in user changes.
  useEffect(() => {
    clearSession();
    setSessionDidExpire(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // ── Public API ──────────────────────────────────────────────────────────────

  // Start (or resume) a draft session. Returns the session, or throws an error
  // with code "sessionExpired" on the PATH 2 forfeit so the overview can show it.
  const startSession = useCallback(
    async (poolID, sportId, slot, dayNumber) => {
      if (!user) {
        const err = new Error("You must be signed in to draft.");
        err.code = "notSignedIn";
        throw err;
      }

      // PATH 1 — live session already in memory for this pool/day.
      const cur = sessionRef.current;
      if (cur && cur.poolID === poolID && cur.dayNumber === dayNumber && !cur.isComplete) {
        return cur;
      }

      // PATH 2 — Firestore has an incomplete session but nothing is in memory:
      // the tab was refreshed/closed mid-draft. Forfeit (0) and block re-entry.
      const existing = await loadDraftSession(poolID, dayNumber, user.uid);
      if (existing && !existing.isComplete) {
        await writeScore({
          poolID,
          sportId,
          dayNumber,
          teamRating: 0,
          chemistryPoints: 0,
          draftedPlayers: [],
          draftedPositions: [],
          timedOut: true,
        });
        const err = new Error("Your draft session expired. Your score was recorded as 000.");
        err.code = "sessionExpired";
        throw err;
      }

      // PATH 3 — genuine fresh start.
      const now = new Date();
      const expiresAt = new Date(now.getTime() + DRAFT_SECONDS * 1000);
      const session = {
        poolID,
        sportId,
        slot,
        userID: user.uid,
        dayNumber,
        startedAt: now,
        expiresAt,
        isComplete: false,
      };
      await writeDraftSession({
        poolID,
        sportId,
        userID: user.uid,
        dayNumber,
        startedAt: now,
        expiresAt,
      });

      sessionRef.current = session;
      draftedRef.current = [];
      expiredRef.current = false;
      setDraftSession(session);
      setDraftedPlayers([]);
      setCurrentPositionIndex(0);
      setSessionDidExpire(false);
      startTimer(expiresAt);
      return session;
    },
    [user, writeScore, startTimer]
  );

  // Keep the manager in sync after each pick so backing out + returning resumes
  // at the exact spot (mirrors KnockoutManager.recordPick).
  const recordPick = useCallback((players, newPositionIndex) => {
    draftedRef.current = players;
    setDraftedPlayers(players);
    setCurrentPositionIndex(newPositionIndex);
  }, []);

  // Submit a completed draft. Writes entry + standing, then clears the session.
  const submitScore = useCallback(
    async ({ teamRating, chemistryPoints, draftedPlayers: players, draftedPositions }) => {
      const session = sessionRef.current;
      if (!session) return;
      await writeScore({
        poolID: session.poolID,
        sportId: session.sportId,
        dayNumber: session.dayNumber,
        teamRating,
        chemistryPoints,
        draftedPlayers: players,
        draftedPositions,
        timedOut: false,
      });
      clearSession();
    },
    [writeScore, clearSession]
  );

  const value = {
    draftSession,
    timeRemaining,
    draftedPlayers,
    currentPositionIndex,
    sessionDidExpire,
    startSession,
    recordPick,
    submitScore,
    clearSession,
  };

  return <KnockoutContext.Provider value={value}>{children}</KnockoutContext.Provider>;
}

export function useKnockoutSession() {
  const ctx = useContext(KnockoutContext);
  if (!ctx) throw new Error("useKnockoutSession must be used within KnockoutProvider");
  return ctx;
}
